import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography, Alert, Divider, Button } from '@mui/material';
import API from '../../services/api';
import ButtonSubmit from '../../components/ButtonSubmit';
import { Edit as EditIcon } from '@mui/icons-material';

export default function EditProfileForm({
  setOpen,
  onSaved,
  getMePath = 'users/me/',
  updateMePath = 'users/update_me/',
}) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [user, setUser] = useState({ first_name: '', last_name: '', email: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get(getMePath);
        setUser({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
        });
      } catch (_) {
        setMsg({ type: 'error', text: 'No se pudo cargar el perfil' });
      }
    };
    load();
  }, [getMePath]);

  const emailError = useMemo(() => {
    if (!user.email) return 'El correo es obligatorio';
    // validación ligera
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email);
    return ok ? '' : 'Correo inválido';
  }, [user.email]);

  const canSave = useMemo(() => {
    return Boolean(user.first_name?.trim() && user.last_name?.trim() && !emailError);
  }, [user.first_name, user.last_name, emailError]);

  const saveProfile = async () => {
    setMsg({ type: '', text: '' });
    if (!canSave) {
      setMsg({ type: 'error', text: 'Revisa los campos obligatorios.' });
      return;
    }
    try {
      setLoading(true);
      await API.patch(updateMePath, user);
      setMsg({ type: 'success', text: 'Cambios guardados' });
      onSaved && onSaved(user);
      setOpen && setOpen(false);
    } catch (_) {
      setMsg({ type: 'error', text: 'No se pudo guardar' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        Editar información
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          label="Nombre"
          value={user.first_name}
          onChange={(e) => setUser({ ...user, first_name: e.target.value })}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label="Apellido"
          value={user.last_name}
          onChange={(e) => setUser({ ...user, last_name: e.target.value })}
          size="small"
          fullWidth
        />
      </Stack>

      <TextField
        label="Correo"
        type="email"
        value={user.email}
        onChange={(e) => setUser({ ...user, email: e.target.value })}
        size="small"
        fullWidth
        error={Boolean(emailError)}
        helperText={emailError || ' '}
      />

      {msg.text && (
        <Alert
          severity={msg.type === 'success' ? 'success' : 'error'}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          {msg.text}
        </Alert>
      )}

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
        <ButtonSubmit
          name="Guardar"
          icon={EditIcon}
          loading={loading}
          color="primary"
          onClick={saveProfile}
          disabled={!canSave}
          fullWidth={true}            // el botón principal se ve sólido
        />
        <Button
          variant="outlined"
          onClick={() => setOpen && setOpen(false)}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          fullWidth
        >
          Cerrar
        </Button>
      </Stack>
    </Box>
  );
}
